import { Alert, Box, Checkbox, FormControlLabel, InputAdornment, Stack, TextField } from '@mui/material'
import { Modal } from '../../customComponents/Modal'
import { type SyntheticEvent, useEffect, useState } from 'react'
import { type FieldError } from '../../../hooks/use-fetch'
import { toast } from 'react-toastify'
import type { AddOrEditAnnualPlanMeetingRequest } from '../../../models/requestObjects/AnnualPlanRequestObjects'
import { LocationSuggestBox } from '../../customComponents/bingmaps/LocationSuggestBox'
import { MapStateName, type SelectOption } from '@oaisd/michdev.components.react'
import type { Address } from '../../../models/Address'
import MUIAutoComplete from '../../customComponents/MUIAutoComplete'
import { AnnualPlanRole } from '../../../models/AnnualPlanRole'
import type { AnnualPlanTeamMember } from '../../../models/AnnualPlanTeamMember'
import { DatePickerWithLabel } from '../../customComponents/DatePickerWithLabel'
import { RemoveOffset, SetTimeWithoutChangingDate } from '../../../utils/dateUtils'
import { ActivityEditSession } from './priorityActivities/ActivityEditSession'
import { PlanOrganizationTaskName } from '../../../models/PlanOrganizationTask'
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { type Dayjs } from 'dayjs'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

interface Props {
  orgId: number
  annualPlanId: number
  editMeeting?: AddOrEditAnnualPlanMeetingRequest
  teamMembers: AnnualPlanTeamMember[]
  isImplementationPhase?: boolean
  onClose: (isUpdate: boolean, submittedForm: AddOrEditAnnualPlanMeetingRequest, isTracking: boolean) => void
  onCancel: () => void
}

export function AddMeetingModal (props: Props): JSX.Element {
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([])
  const [contactOptions, setContactOptions] = useState<Array<{ id: string | number, name: string }>>([])
  const [selectedPrimaryContact, setSelectedPrimaryContact] = useState<SelectOption<number> | null>()
  const [isLoading, setIsLoading] = useState(true)
  const [isVirtualMeeting, setIsVirtualMeeting] = useState(props.editMeeting?.isVirtual ?? false)
  const [meetingTime, setMeetingTime] = useState<Dayjs | null>(props.editMeeting?.meetingDate == null ? null : dayjs(props.editMeeting?.meetingDate))
  const [endMeetingTime, setEndMeetingTime] = useState<Dayjs | null>(props.editMeeting?.meetingEndDate == null ? null : dayjs(props.editMeeting?.meetingEndDate))
  const [selectedLocation, setSelectedLocation] = useState<Address>()
  const isImplementationPhase = props.isImplementationPhase ?? false
  const [budgetAmount, setBudgetAmount] = useState<string>(props.editMeeting != null ? (props.editMeeting.adjustedBudgetAmount != null && props.editMeeting.adjustedBudgetAmount !== '' ? props.editMeeting.adjustedBudgetAmount : props.editMeeting.budgetRequestAmount) : '')

  const emptyAddress: Address = {
    id: 0,
    street: '',
    city: '',
    state: '',
    zip: '',
    apartment: undefined
  }
  const [editingAddress, setEditingAddress] = useState<Address>(emptyAddress)

  const [formValues, setFormValues] = useState<AddOrEditAnnualPlanMeetingRequest>(props.editMeeting ?? {
    annualPlanId: props.annualPlanId,
    orgId: props.orgId,
    rcnOperationId: 0,
    title: '',
    description: '',
    meetingDate: undefined,
    meetingEndDate: null,
    budgetRequestAmount: '',
    adjustedBudgetAmount: undefined,
    actualBudgetAmount: undefined,
    primaryContactId: undefined,
    location: emptyAddress,
    isVirtual: false,
    virtualLink: ''
  })

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const teamOptions: Array<{ id: string | number, name: string }> = []
      props.teamMembers.forEach(tm => {
        if (tm.annualPlanRole === AnnualPlanRole.PlanAdministrator || tm.annualPlanRole === AnnualPlanRole.PlanContributor) {
          teamOptions.push({ id: tm.userId, name: `${tm.firstName} ${tm.lastName}` })
        }
      })
      setContactOptions(teamOptions)
      if (props.editMeeting != null) {
        setEditingAddress(props.editMeeting.location ?? emptyAddress)
        setSelectedLocation(props.editMeeting.location)
        const primaryContact: number = Number(props.editMeeting.primaryContactId)
        const selected = teamOptions.find(filt => filt.id === primaryContact)
        if (selected != null) {
          setSelectedPrimaryContact({ id: Number(selected.id), name: selected.name })
        }
      }
      setIsLoading(false)
    }

    void fetchData()
  }, [props.editMeeting])

  const handleSave = async (): Promise<void> => {
    if (!validateForm()) {
      return
    }

    const loc = selectedLocation ?? emptyAddress
    if (selectedLocation?.city === '' && selectedLocation?.street === '' && editingAddress.street !== '') {
      loc.street = editingAddress.street
      loc.id = 0
    }
    const completedForm: AddOrEditAnnualPlanMeetingRequest = { ...formValues, location: loc, isVirtual: isVirtualMeeting, isImplementationPhase }

    if (props.editMeeting == null) {
      completedForm.budgetRequestAmount = budgetAmount
    }
    if (props.isImplementationPhase ?? false) {
      completedForm.adjustedBudgetAmount = budgetAmount
    }

    const updatedForm = prepareMeetingDates(completedForm, meetingTime, endMeetingTime)

    const isUpdate = props.editMeeting != null
    props.onClose(isUpdate, updatedForm, false)
  }

  const handleSetEndDate = (date: Dayjs | null): void => {
    let meetingDate = formValues.meetingDate != null ? new Date(formValues.meetingDate) : new Date()
    if (date != null) {
      meetingDate = SetTimeWithoutChangingDate(meetingDate, date.toDate())
      setEndMeetingTime(dayjs(meetingDate))
    } else {
      setEndMeetingTime(null)
    }
  }

  const prepareMeetingDates = (
    form: AddOrEditAnnualPlanMeetingRequest,
    meetingTime: Dayjs | null,
    endMeetingTime: Dayjs | null
  ): AddOrEditAnnualPlanMeetingRequest => {
    const updatedForm = { ...form }

    let meetingDate = new Date(updatedForm.meetingDate ?? new Date())
    if (meetingTime != null) {
      meetingDate = SetTimeWithoutChangingDate(meetingDate, meetingTime.toDate())
      updatedForm.meetingDate = RemoveOffset(meetingDate)
    }
    if (endMeetingTime != null) {
      meetingDate = SetTimeWithoutChangingDate(meetingDate, endMeetingTime.toDate())
      updatedForm.meetingEndDate = RemoveOffset(meetingDate)
    } else {
      updatedForm.meetingEndDate = null
    }

    return updatedForm
  }

  function validateForm (): boolean {
    const formFieldErrors: FieldError[] = []

    let invalidDate: boolean = false
    if (meetingTime != null && formValues.meetingDate != null) {
      let meetingDate = new Date(formValues.meetingDate ?? new Date())
      meetingDate = SetTimeWithoutChangingDate(meetingDate, meetingTime.toDate())
      const nowDate = new Date()
      if (meetingDate < nowDate && !isImplementationPhase) {
        invalidDate = true
      }
    }

    if (formValues.title === '') {
      formFieldErrors.push({ fieldName: 'title', message: '' })
    }

    if (formValues.meetingDate == null) {
      formFieldErrors.push({ fieldName: 'meetingDate', message: '' })
    }

    if (meetingTime == null) {
      formFieldErrors.push({ fieldName: 'meetingTime', message: '' })
    }

    if (endMeetingTime?.isBefore(meetingTime) === true) {
      formFieldErrors.push({ fieldName: 'endMeetingTime', message: 'End time must be after start time.' })
    }

    if (!formValues.isVirtual) {
      if (selectedLocation == null || (selectedLocation.street === '' && selectedLocation.zip === '')) {
        if (editingAddress.street === '') {
          formFieldErrors.push({ fieldName: 'address', message: '' })
        }
      }
    }

    if (formValues.primaryContactId == null) {
      formFieldErrors.push({ fieldName: 'primaryContactId', message: '' })
    }

    if (budgetAmount === '' && (props.isImplementationPhase ?? false)) {
      formFieldErrors.push({ fieldName: 'budgetRequestAmount', message: '' })
    }

    setFieldErrors(formFieldErrors)
    if (formFieldErrors.length > 0 || invalidDate) {
      if (invalidDate) {
        toast.error('Meeting must be in the future')
      }
      if (formFieldErrors.length > 0) {
        toast.error('Please complete all required fields.')
      }
      return false
    }

    return true
  }

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (e.target.name === 'budgetRequestAmount') {
      setBudgetAmount(e.target.value)
    } else {
      setFormValues({
        ...formValues,
        [e.target.name]: e.target.value
      })
    }

    setFieldErrors(fieldErrors.filter(f => f.fieldName !== e.target.name))
  }

  const handleContactChange = (e: SyntheticEvent, value: { id: string | number, name: string } | null): void => {
    const contact = contactOptions.find(d => d.id === value?.id) ?? null

    if (contact == null) {
      return
    }

    setSelectedPrimaryContact({ id: Number(contact.id), name: contact.name })

    const selected: { id: number, name: string } = { id: Number(contact?.id), name: contact?.name }
    setFormValues({
      ...formValues,
      primaryContactId: selected.id
    })
  }

  const handlePlaceSelected = async (place: Microsoft.Maps.ISuggestionResult): Promise<void> => {
    const newplace: Address = {
      id: 0,
      street: place.address.addressLine,
      city: place.address.locality,
      apartment: '',
      state: MapStateName(place.address.adminDistrict),
      zip: place.address.postalCode
    }
    setEditingAddress(newplace)
    setSelectedLocation(newplace)
  }

  const handleLocChange = (e: any): void => {
    if (e.target.value === '') {
      setSelectedLocation(emptyAddress)
      setEditingAddress(emptyAddress)
    } else {
      setEditingAddress({ ...editingAddress, street: e.target.value })
    }
  }

  function handleDateChanged (newValue: Date | null): void {
    // If no date, just update the form value
    if (newValue === null) {
      setFormValues({ ...formValues, meetingDate: undefined })
      return
    }

    // Update meetingTime and endMeetingTime to the new date with existing times
    meetingTime !== null && setMeetingTime(setDateWithTime(newValue, meetingTime))
    endMeetingTime !== null && setEndMeetingTime(setDateWithTime(newValue, endMeetingTime))

    setFormValues({ ...formValues, meetingDate: newValue })
  }

  const setDateWithTime = (date: Date, time: Dayjs | null): Dayjs => {
    if (time === null) return dayjs(date)
    return dayjs(date)
      .hour(time.hour())
      .minute(time.minute())
  }

  const handleVirtualChecked = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    setIsVirtualMeeting(e.target.checked)

    if (e.target.checked) {
      setFormValues({
        ...formValues,
        isVirtual: e.target.checked
      })

      setFieldErrors(fieldErrors.filter(f => f.fieldName !== 'address'))
    } else {
      setFormValues({
        ...formValues,
        isVirtual: e.target.checked,
        virtualLink: ''
      })
    }
  }

  const content = <Box minWidth='200px'>
    {
      props.isImplementationPhase === true && props.editMeeting == null &&
        <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
          backgroundColor: 'var(--blue-500)',
          color: 'white',
          mb: '.5em',
          fill: 'white'
        }} severity="info"
        >The addition of this item will prompt a review from the START team.
        </Alert>
    }
    <TextField
      variant='filled'
      data-testid='meetingTitle'
      fullWidth
      value={formValues.title}
      onChange={handleFieldUpdate}
      placeholder='Name of the meeting'
      label={'Meeting Title'}
      name={'title'}
      required
      error={fieldErrors.some((error) => error.fieldName === 'title')}
    />

    <TextField
      variant='filled'
      data-testid='meetingDescription'
      fullWidth
      multiline
      value={formValues.description}
      minRows={3}
      name='description'
      label='Meeting Description'
      onChange={handleFieldUpdate}
      sx={{ mt: '.75em' }}
    />

    <DatePickerWithLabel
      disablePast={!isImplementationPhase}
      FormControlProps={{ variant: 'outlined', sx: { marginTop: '.75em', width: '225px' } }}
      name='meetingDate'
      label='Meeting Date'
      required
      error={fieldErrors.some((error) => error.fieldName === 'meetingDate')}
      dataTestId='meetingDate'
      onChange={handleDateChanged}
      sx={{ mt: '.75em' }}
      value={formValues.meetingDate ?? null}
    />

    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction='row' gap='1em'>
        <TimePicker
          sx={{ mt: '.75em', width: '225px' }}
          slotProps={{
            textField: {
              name: 'meetingTime',
              required: true,
              error: fieldErrors.some((error) => error.fieldName === 'meetingTime'),
              variant: 'filled',
              inputProps: {
                'data-testid': 'meetingTime'
              }
            }
          }}
          label='Start Meeting Time'
          value={meetingTime}
          onChange={(newValue) => { setMeetingTime(newValue) }}
        />

        <TimePicker
          sx={{ mt: '.75em', width: '225px' }}
          slotProps={{
            textField: {
              name: 'endMeetingTime',
              required: false,
              helperText: fieldErrors.find(f => f.fieldName === 'endMeetingTime')?.message,
              error: fieldErrors.some((error) => error.fieldName === 'endMeetingTime'),
              variant: 'filled',
              inputProps: {
                'data-testid': 'endMeetingTime'
              }
            }
          }}
          label='End Meeting Time'
          value={endMeetingTime}
          onChange={(newValue) => { handleSetEndDate(newValue) }}
        />
      </Stack>
    </LocalizationProvider>

    <Box>
      <FormControlLabel
        label={'Virtual Meeting?'}
        sx={{ mt: '.75em', width: '225px' }}
        control={
          <Checkbox
            sx={{ color: 'var(--grey-700)' }}
            checked={isVirtualMeeting}
            onChange={handleVirtualChecked}
            data-testid='isVirtualCheckbox'
          />
        }
      />
    </Box>

    { isVirtualMeeting && <TextField
      variant='filled'
      data-testid='virtualLink'
      fullWidth
      value={formValues.virtualLink}
      onChange={handleFieldUpdate}
      label={'Virtual Link'}
      name={'virtualLink'}
    />
    }

    <Box sx={{ my: '.75em' }}>
      <LocationSuggestBox editData={editingAddress}
        integratedLabel={true}
        error={fieldErrors.some((error) => error.fieldName === 'address')}
        required={!isVirtualMeeting}
        onChange={handleLocChange}
        onPlaceSelected={handlePlaceSelected}
      />
    </Box>

    <MUIAutoComplete
      required
      label='Primary Contact'
      name='memberSelect'
      value={selectedPrimaryContact ?? undefined}
      onChange={handleContactChange}
      options={contactOptions}
      error={fieldErrors.some((error) => error.fieldName === 'primaryContactId')}
      sx={{ width: '100%' }}
    />

    <TextField
      sx={{ mt: '.75em', width: '250px' }}
      variant='filled'
      onChange={handleFieldUpdate}
      label={'Budget Amount'}
      data-testid='budgetRequestAmount'
      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
      name={'budgetRequestAmount'}
      required
      error={fieldErrors.some((error) => error.fieldName === 'budgetRequestAmount')}
      value={budgetAmount}
    />

    {props.editMeeting != null &&
      <ActivityEditSession
        activityId={props.editMeeting.id ?? 0}
        activityName={PlanOrganizationTaskName.RcnOperations}
        onClose={() => { props.onCancel() }}
      />
    }
  </Box>

  if (isLoading) {
    return <></>
  }

  const getTitle = (): string => {
    if (props.editMeeting != null) {
      return isImplementationPhase ? 'Update Meeting' : 'Edit Meeting'
    }

    return 'Add Meeting'
  }

  return <Modal
    open={true}
    maxWidth='md'
    title={getTitle()}
    confirmButtonText='Save'
    bodyContent={content}
    onClose={() => { props.onCancel() }}
    onConfirm={handleSave}
  />
}
